import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Formik, FormikErrors } from 'formik';
import { axiosKK } from '../../services/networkRequest';
import {
  CreateLogisticsUserRequest,
  LogisticsUserRole,
  PartnerUserAccess,
} from '../../models/APIModels';
import errorMessage from '../../services/errorMessage';
import SubmitButton from '../SubmitButton';
import Label from '../Label';

interface Props {
  show: boolean;
  partnerOptions: { id: number; name: string }[];
  defaultPartnerId?: number;
  close: () => void;
  onSuccess: (user: PartnerUserAccess) => void;
}

interface FormValues {
  partnerId: number | '';
  email: string;
  name: string;
  phone: string;
  password: string;
  role: LogisticsUserRole | '';
  logisticsDashboardEnabled: boolean;
}

const ROLES: { value: LogisticsUserRole; label: string }[] = [
  { value: 'MASTER', label: 'Master' },
  { value: 'DRIVER', label: 'Driver' },
  { value: 'WAREHOUSE_STAFF', label: 'Warehouse Staff' },
];

function validate(values: FormValues): FormikErrors<FormValues> {
  const errors: FormikErrors<FormValues> = {};
  if (!values.partnerId) errors.partnerId = 'Partner is required';
  if (!values.email) errors.email = 'Email is required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errors.email = 'Invalid email';
  if (!values.name) errors.name = 'Name is required';
  if (!values.password) errors.password = 'Password is required';
  else if (values.password.length < 8) errors.password = 'Password must be at least 8 characters';
  if (!values.role) errors.role = 'Role is required';
  return errors;
}

export default function CreateLogisticsUserModal({
  show,
  partnerOptions,
  defaultPartnerId,
  close,
  onSuccess,
}: Props) {
  const [networkError, setNetworkError] = React.useState<string>();

  useEffect(() => {
    if (!show) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [show, close]);

  if (!show) return null;

  const initialValues: FormValues = {
    partnerId: defaultPartnerId ?? '',
    email: '',
    name: '',
    phone: '',
    password: '',
    role: '',
    logisticsDashboardEnabled: true,
  };

  return ReactDOM.createPortal(
    <div className="logistics-modal__overlay" onClick={close}>
      <div
        className="logistics-modal__container"
        onClick={(e) => e.stopPropagation()}
      >
        <Formik
          initialValues={initialValues}
          validate={validate}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setNetworkError(undefined);
            const partnerId = Number(values.partnerId);
            const payload: CreateLogisticsUserRequest = {
              email: values.email.trim(),
              name: values.name.trim(),
              role: values.role as LogisticsUserRole,
              password: values.password,
              logisticsDashboardEnabled: values.logisticsDashboardEnabled,
            };
            if (values.phone.trim()) payload.phone = values.phone.trim();

            axiosKK
              .post<PartnerUserAccess>(
                `/logistics/admin/partners/${partnerId}/users`,
                payload
              )
              .then(({ data }) => {
                onSuccess(data);
                resetForm();
                close();
              })
              .catch((error) => {
                errorMessage(setNetworkError)(error);
              })
              .finally(() => {
                setSubmitting(false);
              });
          }}
        >
          {({ values, errors, touched, isSubmitting, handleChange, handleBlur, handleSubmit, isValid, dirty }) => (
            <form onSubmit={handleSubmit} className="logistics-modal__form" name="create-logistics-user-form">
              <div className="logistics-modal__header">
                <div className="logistics-modal__header-info">
                  <h3>Create Logistics User</h3>
                  <span className="logistics-modal__subtitle">
                    Partner master, driver, or warehouse staff
                  </span>
                </div>
                <button className="logistics-modal__close" type="button" onClick={close}>
                  &times;
                </button>
              </div>

              <div className="logistics-modal__body">
                <div className="logistics-modal__section">
                  <div className="logistics-modal__field">
                    <Label htmlFor="partnerId">Partner</Label>
                    <select
                      className="logistics-modal__select"
                      id="partnerId"
                      name="partnerId"
                      value={values.partnerId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <option value="">— Select partner —</option>
                      {partnerOptions.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    {touched.partnerId && errors.partnerId && (
                      <span className="error">{errors.partnerId}</span>
                    )}
                  </div>

                  <div className="logistics-modal__field">
                    <Label htmlFor="role">Role</Label>
                    <select
                      className="logistics-modal__select"
                      id="role"
                      name="role"
                      value={values.role}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <option value="">— Select role —</option>
                      {ROLES.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                    {touched.role && errors.role && (
                      <span className="error">{errors.role}</span>
                    )}
                  </div>
                </div>

                <div className="logistics-modal__section">
                  <div className="logistics-modal__field">
                    <Label htmlFor="name">Name</Label>
                    <input
                      className="logistics-modal__input"
                      type="text"
                      id="name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.name && errors.name && (
                      <span className="error">{errors.name}</span>
                    )}
                  </div>

                  <div className="logistics-modal__field">
                    <Label htmlFor="email">Email</Label>
                    <input
                      className="logistics-modal__input"
                      type="email"
                      id="email"
                      name="email"
                      autoComplete="off"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.email && errors.email && (
                      <span className="error">{errors.email}</span>
                    )}
                  </div>

                  <div className="logistics-modal__field">
                    <Label htmlFor="phone">Phone (optional)</Label>
                    <input
                      className="logistics-modal__input"
                      type="tel"
                      id="phone"
                      name="phone"
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>

                  <div className="logistics-modal__field">
                    <Label htmlFor="password">Password</Label>
                    <input
                      className="logistics-modal__input"
                      type="password"
                      id="password"
                      name="password"
                      autoComplete="new-password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.password && errors.password && (
                      <span className="error">{errors.password}</span>
                    )}
                  </div>
                </div>

                <div className="logistics-modal__section">
                  <label className="logistics-modal__checkbox">
                    <input
                      type="checkbox"
                      name="logisticsDashboardEnabled"
                      checked={values.logisticsDashboardEnabled}
                      onChange={handleChange}
                    />
                    <span>Enable dashboard access immediately</span>
                  </label>
                </div>

                {networkError && <span className="error">{networkError}</span>}
              </div>

              <div className="logistics-modal__footer">
                <SubmitButton
                  disabled={!isValid || !dirty}
                  isSubmitting={isSubmitting}
                >
                  Create User
                </SubmitButton>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>,
    document.body
  );
}
